import { Html, SoftShadows, useEnvironment } from "@react-three/drei";
import { useThree, useFrame } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { LightProbeGenerator } from "three/examples/jsm/lights/LightProbeGenerator.js";
import { ToneMapping } from "@react-three/postprocessing";

import {
  EffectComposer,
  Autofocus,
  Vignette,
  Bloom,
  N8AO,
  SMAA,
} from "@react-three/postprocessing";

// Helper to disable normal map mipmaps for better quality
export function disableNormalMipmapsOnScene(root) {
  if (!root) return;

  root.traverse((o) => {
    if (!o.isMesh) return;

    const mats = Array.isArray(o.material) ? o.material : [o.material];
    mats.forEach((m) => {
      if (!m) return;

      const n = m.normalMap;
      if (!n) return;

      n.generateMipmaps = false;
      n.minFilter = THREE.LinearFilter;
      n.magFilter = THREE.LinearFilter;
      n.needsUpdate = true;

      m.needsUpdate = true;
    });
  });
}

/* =============================================================================
   QUALITY CONFIG (moved as-is)
============================================================================= */

export const QUALITY_PRESETS = {
  ultra: {
    id: "ultra",
    label: "Ultra",
    dpr: [1, 2],
    composerMultisampling: 8,
    softShadows: { enabled: true, focus: 0.0, samples: 32, size: 18 },
    shadows: { mapSize: 1024, type: THREE.PCFShadowMap },
    post: { n8ao: { enabled: true, quality: "high" }, smaa: true },
  },
  high: {
    id: "high",
    label: "High",
    dpr: [1, 1.75],
    composerMultisampling: 4,
    softShadows: { enabled: true, focus: 0.0, samples: 24, size: 16 },
    shadows: { mapSize: 768, type: THREE.PCFShadowMap },
    post: { n8ao: { enabled: true, quality: "medium" }, smaa: true },
  },
  medium: {
    id: "medium",
    label: "Medium",
    dpr: [1, 1.25],
    composerMultisampling: 0,
    softShadows: { enabled: true, focus: 0.0, samples: 16, size: 14 },
    shadows: { mapSize: 512, type: THREE.PCFShadowMap },
    post: { n8ao: { enabled: true, quality: "low" }, smaa: true },
  },
  low: {
    id: "low",
    label: "Low",
    dpr: [1, 1],
    composerMultisampling: 0,
    softShadows: { enabled: false, focus: 0.0, samples: 0, size: 0 },
    shadows: { mapSize: 256, type: THREE.PCFShadowMap },
    post: { n8ao: { enabled: false, quality: "low" }, smaa: true },
  },
};

export const ADAPTIVE_QUALITY_CONFIG = {
  enabled: true,
  startPresetId: "low",
  sampleWindowSeconds: 1.0,
  warmupMs: 4000,
  allowUpshift: true,
  upshiftAboveFPS: 58,
  downshiftBelowFPS: 45,
  minChangeIntervalMs: 1500,
};

const PRESET_ORDER = ["low", "medium", "high", "ultra"];

export function clampPresetId(id) {
  return QUALITY_PRESETS[id] ? id : ADAPTIVE_QUALITY_CONFIG.startPresetId;
}

export function nextLowerPresetId(currentId) {
  const i = PRESET_ORDER.indexOf(currentId);
  if (i <= 0) return currentId;
  return PRESET_ORDER[i - 1];
}

export function nextHigherPresetId(currentId) {
  const i = PRESET_ORDER.indexOf(currentId);
  if (i < 0 || i >= PRESET_ORDER.length - 1) return currentId;
  return PRESET_ORDER[i + 1];
}

/* =============================================================================
   FPS sampling + adaptive controller (moved as-is)
============================================================================= */

function useFpsSampler({ enabled, sampleWindowSeconds, onSample }) {
  const acc = useRef({ t: 0, frames: 0 });

  useFrame((_, delta) => {
    if (!enabled) return;

    acc.current.t += delta;
    acc.current.frames += 1;

    if (acc.current.t >= sampleWindowSeconds) {
      const fps = acc.current.frames / acc.current.t;
      acc.current.t = 0;
      acc.current.frames = 0;
      onSample?.(fps);
    }
  });
}

export function AdaptiveQualityController({ presetId, setPresetId, config }) {
  const startedAtRef = useRef(performance.now());
  const lastChangeAtRef = useRef(0);
  const lockedRef = useRef(false);
  const hasUpshiftedRef = useRef(false);

  const calibrationDurationMs = 20000; // 20 seconds

  const onSample = useCallback(
    (fps) => {
      const now = performance.now();

      if (!config.enabled || lockedRef.current) return;

      // Lock permanently after calibration window
      if (now - startedAtRef.current > calibrationDurationMs) {
        lockedRef.current = true;
        console.log("Adaptive quality locked at:", presetId);
        return;
      }

      // Warmup period
      if (now - startedAtRef.current < (config.warmupMs ?? 0)) return;

      const minInterval = config.minChangeIntervalMs ?? 1500;
      if (now - lastChangeAtRef.current < minInterval) return;

      /* ----------------------------- */
      /* Downshift logic               */
      /* ----------------------------- */

      if (fps < config.downshiftBelowFPS) {
        // Prevent oscillation after first successful upshift
        if (hasUpshiftedRef.current) return;

        const next = nextLowerPresetId(presetId);
        if (next !== presetId) {
          setPresetId(next);
          lastChangeAtRef.current = now;
        }
        return;
      }

      /* ----------------------------- */
      /* Upshift logic                 */
      /* ----------------------------- */

      if (config.allowUpshift && fps > config.upshiftAboveFPS) {
        const next = nextHigherPresetId(presetId);

        if (next !== presetId) {
          setPresetId(next);
          lastChangeAtRef.current = now;

          // Mark that we successfully climbed
          hasUpshiftedRef.current = true;
        }
      }
    },
    [presetId, setPresetId, config],
  );

  useFpsSampler({
    enabled: config.enabled,
    sampleWindowSeconds: config.sampleWindowSeconds,
    onSample,
  });

  return null;
}

/* =============================================================================
   HUD (moved as-is, includes HIGH PERFORMANCE label)
============================================================================= */

export function PerfHud({
  presetId,
  forcedPresetId,
  renderEnabled,
  enabled = true,
  sampleWindowSeconds = 0.5,
}) {
  const [fps, setFps] = useState(0);
  const acc = useRef({ t: 0, frames: 0 });

  useFrame((_, delta) => {
    if (!enabled) return;

    acc.current.t += delta;
    acc.current.frames += 1;

    if (acc.current.t >= sampleWindowSeconds) {
      const value = acc.current.frames / acc.current.t;
      setFps(Math.round(value));
      acc.current.t = 0;
      acc.current.frames = 0;
    }
  });

  if (!enabled) return null;

  const modeLabel = renderEnabled
    ? forcedPresetId
      ? `FORCED: ${forcedPresetId}`
      : "AUTO"
    : "HIGH PERFORMANCE";

  const controlsHint = renderEnabled
    ? "(1-4 force, 0 auto, [ / ] cycle, 5 high perf)"
    : "(5 full render, 1-4 force, 0 auto, [ / ] cycle)";

  return <></>;
  // return (
  //   <Html
  //     prepend
  //     style={{
  //       position: "fixed",
  //       top: 10,
  //       left: 10,
  //       padding: "8px 10px",
  //       borderRadius: "8px",
  //       background: "rgba(0,0,0,0.55)",
  //       color: "white",
  //       fontFamily:
  //         "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  //       fontSize: "12px",
  //       lineHeight: 1.25,
  //       pointerEvents: "none",
  //       userSelect: "none",
  //       zIndex: 9999,
  //       whiteSpace: "pre",
  //     }}
  //   >
  //     {`FPS: ${fps}\nPreset: ${presetId}\nMode: ${modeLabel}\n${controlsHint}`}
  //   </Html>
  // );
}

/* =============================================================================
   Fallback IBL (moved as-is)
============================================================================= */

export function FallbackIBL({ enabled = true, intensity = 1.0, isNight }) {
  const { gl, scene } = useThree();

  useEffect(() => {
    if (!enabled) return;

    const pmrem = new THREE.PMREMGenerator(gl);
    const envScene = new THREE.Scene();
    envScene.background = new THREE.Color(0xffffff);

    const rt = pmrem.fromScene(envScene, 0.04);
    scene.environment = rt.texture;

    return () => {
      if (scene.environment === rt.texture) scene.environment = null;
      rt.dispose();
      pmrem.dispose();
    };
  }, [gl, scene, enabled, isNight]);

  useEffect(() => {
    if (!enabled) return;
    scene.traverse((o) => {
      if (!o.isMesh) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        if ("envMapIntensity" in m) {
          m.envMapIntensity = intensity;
          m.needsUpdate = true;
        }
      });
    });
  }, [scene, enabled, intensity, isNight]);

  return null;
}

/* =============================================================================
   Post / DOF (moved as-is)
============================================================================= */

export function AutoDOF({
  preset,
  enabled = true,
  isNight,
  effectivePresetId,
}) {
  const { camera, invalidate } = useThree();

  const DOF_RAYCAST_EVERY_N_FRAMES = 6;

  const DOF = useMemo(
    () => ({
      bokehScale: 0.8,
      focalLength: 5,
      height: 900,
      smoothTime: 0.5,
      debug: 0,
    }),
    [],
  );

  const fade = useMemo(
    () => ({
      fadeStart: 30,
      fadeEnd: 60,
      response: 0.8,
      epsilon: 0.01,
    }),
    [],
  );

  const target = useMemo(() => new THREE.Vector3(0, 0, 0), []);
  const strengthRef = useRef(1);

  const autofocusRef = useRef(null);
  const frameCounterRef = useRef(0);
  const tmpTargetRef = useRef(new THREE.Vector3());

  useEffect(() => {
    if (!enabled) return;
    invalidate?.();
  }, [invalidate, enabled]);

  useEffect(() => {
    if (!enabled) return;
    invalidate?.();
    const id = requestAnimationFrame(() => invalidate?.());
    return () => cancelAnimationFrame(id);
  }, [invalidate, enabled, isNight]);

  const damp3 = (v, to, lambda, delta) => {
    v.x = THREE.MathUtils.damp(v.x, to.x, lambda, delta);
    v.y = THREE.MathUtils.damp(v.y, to.y, lambda, delta);
    v.z = THREE.MathUtils.damp(v.z, to.z, lambda, delta);
  };

  useFrame((_, delta) => {
    if (!enabled) return;

    const dist = camera.position.distanceTo(target);
    const tRaw = (dist - fade.fadeStart) / (fade.fadeEnd - fade.fadeStart);
    const t = THREE.MathUtils.clamp(tRaw, 0, 1);
    const smoothT = t * t * (3 - 2 * t);
    const desired = 1 - smoothT;

    strengthRef.current = THREE.MathUtils.damp(
      strengthRef.current,
      desired,
      fade.response,
      delta,
    );

    const api = autofocusRef.current;
    const dof = api?.dofRef?.current;

    if (dof) dof.bokehScale = DOF.bokehScale * strengthRef.current;

    if (api) {
      frameCounterRef.current += 1;
      const n = Math.max(1, DOF_RAYCAST_EVERY_N_FRAMES | 0);
      const shouldRaycastThisFrame = frameCounterRef.current % n === 0;

      if (shouldRaycastThisFrame) api.update(delta, false);

      if (dof?.target && api.hitpoint) {
        tmpTargetRef.current.copy(api.hitpoint);
        const follow = 1 / Math.max(0.001, DOF.smoothTime);
        damp3(dof.target, tmpTargetRef.current, follow, delta);
      }
    }
  });

  if (!enabled) return null;

  return (
    <EffectComposer
      multisampling={preset.composerMultisampling}
      depthBuffer
      enableNormalPass
    >
      <ToneMapping
        adaptive={false}
        resolution={256}
        middleGrey={10}
        maxLuminance={16}
        averageLuminance={1}
        adaptationRate={1}
      />
      <Autofocus
        ref={autofocusRef}
        mouse
        manual
        debug={DOF.debug}
        smoothTime={DOF.smoothTime}
        focalLength={DOF.focalLength}
        bokehScale={DOF.bokehScale}
        height={DOF.height}
      />

      {preset.post.n8ao.enabled && (
        <N8AO
          intensity={
            isNight
              ? isPresetMeduimOrLower(effectivePresetId)
                ? 1.5
                : 2.2
              : 2.5
          }
          aoRadius={18}
          distanceFalloff={1.0}
          quality={preset.post.n8ao.quality}
        />
      )}

      <Bloom
        intensity={
          isNight
            ? isPresetMeduimOrLower(effectivePresetId)
              ? 0.1
              : 0.3
            : 0.35
        }
        luminanceThreshold={
          isNight
            ? isPresetMeduimOrLower(effectivePresetId)
              ? 0.4
              : 0.7
            : 0.85
        }
        luminanceSmoothing={0.1}
      />

      <Vignette eskil={false} offset={0.05} darkness={0} />
      {preset.post.smaa && <SMAA />}
    </EffectComposer>
  );
}

/* =============================================================================
   Env + light probe (moved, parameterized)
============================================================================= */

export function EnvWithLightProbe({
  SceneEnvPath,
  background = true,
  probeIntensity = 1.0,
  domeAnglesRef,
  hdriOffsetRef,
  backgroundMode = "blur",
  backgroundBlur = 3,
  isNight,
  effectivePresetId,
}) {
  const { gl, scene } = useThree();
  const envTex = useEnvironment(
    useMemo(() => ({ files: SceneEnvPath }), [SceneEnvPath]),
  );

  const lastAppliedRef = useRef({ x: 999, y: 999, z: 999 });

  useFrame(() => {
    const a = domeAnglesRef?.current;
    if (!a) return;

    const rotX = a.polar - Math.PI / 2;
    const rotY = a.azimuth;
    const rotZ = 0;

    const off = hdriOffsetRef?.current;
    const x = rotX + (off?.x ?? 0);
    const y = rotY + (off?.y ?? 0);
    const z = rotZ + (off?.z ?? 0);

    const last = lastAppliedRef.current;
    const eps = 1e-4;
    if (
      Math.abs(x - last.x) < eps &&
      Math.abs(y - last.y) < eps &&
      Math.abs(z - last.z) < eps
    )
      return;

    last.x = x;
    last.y = y;
    last.z = z;

    if (scene.environmentRotation) scene.environmentRotation.set(x, y, z);
    if (scene.backgroundRotation) scene.backgroundRotation.set(x, y, z);
  });

  useEffect(() => {
    if (!envTex) return;

    scene.background = null;
    scene.environment = null;

    const pmrem = new THREE.PMREMGenerator(gl);
    pmrem.compileEquirectangularShader();

    let envRT;
    if (envTex.isCubeTexture) {
      envRT = pmrem.fromCubemap(envTex);
    } else {
      envTex.mapping = THREE.EquirectangularReflectionMapping;
      pmrem.compileEquirectangularShader();
      envRT = pmrem.fromEquirectangular(envTex);
    }

    scene.environment = envRT.texture;

    let appliedBackground = null;

    if (isNight) {
      appliedBackground = new THREE.Color(
        isPresetMeduimOrLower(effectivePresetId) ? "#6d758d" : "#949cb3",
      );
      scene.background = appliedBackground;
      if ("backgroundBlurriness" in scene) scene.backgroundBlurriness = 0;
      if ("backgroundIntensity" in scene) scene.backgroundIntensity = 0.35;
    } else if (!background || backgroundMode === "off") {
      scene.background = null;
      if ("backgroundBlurriness" in scene) scene.backgroundBlurriness = 0;
      if ("backgroundIntensity" in scene) scene.backgroundIntensity = 1;
    } else if (backgroundMode === "sharp") {
      appliedBackground = envTex;
      scene.background = envTex;
      if ("backgroundBlurriness" in scene) scene.backgroundBlurriness = 0;
      if ("backgroundIntensity" in scene) scene.backgroundIntensity = 1;
    } else if (backgroundMode === "blur") {
      appliedBackground = envRT.texture;
      scene.background = envRT.texture;
      if ("backgroundBlurriness" in scene)
        scene.backgroundBlurriness = backgroundBlur;
      if ("backgroundIntensity" in scene) scene.backgroundIntensity = 1;
    }

    // if (!background || backgroundMode === "off") {
    //   scene.background = null;
    //   if ("backgroundBlurriness" in scene) scene.backgroundBlurriness = 0;
    // } else {
    //   if (isNight) {
    //     // 🌙 NIGHT = solid color background (no HDR override)
    //     scene.background = new THREE.Color("#0b1020");
    //   } else if (backgroundMode === "sharp") {
    //     scene.background = envTex;
    //     if ("backgroundBlurriness" in scene) scene.backgroundBlurriness = 0;
    //   } else if (backgroundMode === "blur") {
    //     scene.background = envRT.texture;
    //     if ("backgroundBlurriness" in scene)
    //       scene.backgroundBlurriness = backgroundBlur;
    //   }
    // }

    let probe = null;
    let disposed = false;

    Promise.resolve(LightProbeGenerator.fromCubeRenderTarget(gl, envRT)).then(
      (generatedProbe) => {
        if (disposed) return;

        probe = generatedProbe;
        probe.intensity = probeIntensity;
        scene.add(probe);
      },
    );

    return () => {
      disposed = true;
      if (probe) scene.remove(probe);
      if (scene.environment === envRT.texture) scene.environment = null;
      if (scene.background === appliedBackground) scene.background = null;
      envRT?.dispose();
      pmrem.dispose();
    };
  }, [
    gl,
    scene,
    envTex,
    background,
    probeIntensity,
    backgroundMode,
    backgroundBlur,
    isNight,
  ]);

  return null;
}

/* =============================================================================
   Light dome orbit + shadow catcher (moved as-is)
============================================================================= */

function CameraAttachedLight({
  lightRefExternal,
  intensity = 1.5,
  isNight,
  effectivePresetId,
}) {
  const { camera } = useThree();
  const lightRef = useRef();
  const targetRef = useRef(new THREE.Object3D());

  // Persistent smoothed values
  const smoothPos = useRef(new THREE.Vector3());
  const smoothTarget = useRef(new THREE.Vector3());
  const cameraDirection = useRef(new THREE.Vector3());
  const desiredTarget = useRef(new THREE.Vector3());

  useEffect(() => {
    if (lightRef.current) {
      lightRef.current.target = targetRef.current;
    }

    // Initialize to camera to avoid jump
    smoothPos.current.copy(camera.position);

    camera.getWorldDirection(cameraDirection.current);
    smoothTarget.current
      .copy(camera.position)
      .add(cameraDirection.current.multiplyScalar(50));
  }, [isNight, camera]);

  useFrame((_, delta) => {
    if (!lightRef.current) return;

    const light = lightRef.current;

    // --- Get desired camera-based values ---
    const desiredPos = camera.position;

    camera.getWorldDirection(cameraDirection.current);

    desiredTarget.current
      .copy(camera.position)
      .add(cameraDirection.current.multiplyScalar(50));

    // --- Smooth follow (cinematic lag) ---
    const followSpeed = 5; // tweak this (lower = more lag, higher = snappier)

    smoothPos.current.lerp(desiredPos, 1 - Math.exp(-followSpeed * delta));
    smoothTarget.current.lerp(
      desiredTarget.current,
      1 - Math.exp(-followSpeed * delta),
    );

    // --- Apply ---
    light.position.copy(smoothPos.current);
    targetRef.current.position.copy(smoothTarget.current);
    targetRef.current.updateMatrixWorld();
  });

  return (
    <>
      <directionalLight
        ref={(node) => {
          lightRef.current = node;
          if (lightRefExternal) lightRefExternal.current = node;
        }}
        intensity={
          isNight ? (isPresetMeduimOrLower(effectivePresetId) ? 2 : 4) : 4.5
        }
        color={
          isNight
            ? isPresetMeduimOrLower(effectivePresetId)
              ? "#496488"
              : "#a4c8f7"
            : "#ffffff"
        }
        castShadow={false}
      />
      <primitive object={targetRef.current} />
    </>
  );
}

export function LightDomeOrbit({
  controlsRef,
  anglesRef,
  hdriOffsetRef,
  target = [0, 0, 0],
  lightRadius = 180,
  helperRadius = 70,
  sensitivity = 0.006,
  minPolar = 0.15,
  maxPolar = 1.35,
  intensity = 1.2,
  shadowProps = {},
  lightRefExternal,
}) {
  const lightRef = useRef();
  const targetRef = useRef();
  const domeRef = useRef();
  const handleRef = useRef();

  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    azimuth: -Math.PI / 2,
    polar: 0.9,
    pointerId: null,
  });

  const targetVec = useMemo(
    () => new THREE.Vector3(target[0], target[1], target[2]),
    [target],
  );

  const applyFromAngles = () => {
    const light = lightRef.current;
    const t = targetRef.current;
    if (!light || !t) return;

    const { azimuth, polar } = dragRef.current;

    const v = new THREE.Vector3().setFromSphericalCoords(
      lightRadius,
      polar,
      azimuth,
    );
    light.position.copy(v).add(t.position);
    light.target.updateMatrixWorld();

    if (anglesRef?.current) {
      anglesRef.current.azimuth = azimuth;
      anglesRef.current.polar = polar;
    }
  };

  useEffect(() => {
    const light = lightRef.current;
    const t = targetRef.current;
    if (!light || !t) return;

    t.position.copy(targetVec);
    light.target = t;
    applyFromAngles();
  }, [targetVec, lightRadius]);

  useEffect(() => {
    domeRef.current?.layers.set(1);
    handleRef.current?.layers.set(1);
  }, []);

  const beginDrag = (e) => {
    if (!e.altKey) return;
    e.stopPropagation();

    dragRef.current.active = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.pointerId = e.pointerId;

    if (controlsRef?.current) controlsRef.current.enabled = false;

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const moveDrag = (e) => {
    if (!dragRef.current.active) return;
    if (dragRef.current.pointerId !== e.pointerId) return;

    e.stopPropagation();

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    dragRef.current.azimuth -= dx * sensitivity;

    dragRef.current.polar = THREE.MathUtils.clamp(
      dragRef.current.polar + dy * sensitivity,
      minPolar,
      maxPolar,
    );

    applyFromAngles();

    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
  };

  const endDrag = (e) => {
    if (!dragRef.current.active) return;
    if (dragRef.current.pointerId !== e.pointerId) return;

    e.stopPropagation();

    dragRef.current.active = false;
    dragRef.current.pointerId = null;

    if (controlsRef?.current) controlsRef.current.enabled = true;

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
  };

  const handlePos = useMemo(() => new THREE.Vector3(), []);
  useFrame(() => {
    const t = targetRef.current;
    if (!t) return;

    const v = new THREE.Vector3().setFromSphericalCoords(
      helperRadius,
      dragRef.current.polar,
      dragRef.current.azimuth,
    );

    handlePos.copy(v).add(t.position);
  });

  return (
    <>
      <object3D ref={targetRef} />

      <directionalLight
        ref={(node) => {
          lightRef.current = node;
          if (lightRefExternal) lightRefExternal.current = node;
        }}
        intensity={intensity}
        castShadow
        shadow-radius={60}
        {...shadowProps}
      />

      <mesh
        ref={domeRef}
        position={target}
        onPointerDown={beginDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <sphereGeometry args={[helperRadius, 24, 16]} />
        <meshBasicMaterial
          wireframe
          transparent
          opacity={0}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh ref={handleRef} position={handlePos}>
        <sphereGeometry args={[4, 16, 16]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </>
  );
}

export function ShadowCatcherConeFadePlane({
  lightRef,
  target = [0, 0, 0],
  size = 3000,
  y = 0,
  opacity = 0.35,
  fadeStart = 120,
  fadeEnd = 320,
  fadePower = 1.0,
  flipFadeDirection = false,
  debug = false,
}) {
  const matRef = useRef(null);
  const shaderRef = useRef(null);

  const targetVec = useMemo(
    () => new THREE.Vector3(target[0], target[1], target[2]),
    [target],
  );

  /* ------------------------------------------------------------------ */
  /*  Patch ShadowMaterial shader                                       */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const mat = matRef.current;
    if (!mat) return;

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uLightDir = { value: new THREE.Vector3(0, -1, 0) };
      shader.uniforms.uTarget = { value: targetVec.clone() };
      shader.uniforms.uFadeStart = { value: fadeStart };
      shader.uniforms.uFadeEnd = { value: fadeEnd };
      shader.uniforms.uFadePower = { value: fadePower };
      shader.uniforms.uFlip = { value: flipFadeDirection ? 1.0 : 0.0 };

      shader.vertexShader = shader.vertexShader.replace(
        "#include <common>",
        `#include <common>
         varying vec3 vWorldPos;`,
      );

      shader.vertexShader = shader.vertexShader.replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
         vec4 worldPos = modelMatrix * vec4(position, 1.0);
         vWorldPos = worldPos.xyz;`,
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <common>",
        `#include <common>
         varying vec3 vWorldPos;
         uniform vec3 uLightDir;
         uniform vec3 uTarget;
         uniform float uFadeStart;
         uniform float uFadeEnd;
         uniform float uFadePower;
         uniform float uFlip;

         float smooth01(float x) {
           return x * x * (3.0 - 2.0 * x);
         }`,
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        "void main() {",
        `void main() {

         vec3 n = vec3(0.0, 1.0, 0.0);
         vec3 L = normalize(uLightDir);

         vec3 planeDir = L - n * dot(L, n);
         float planeLen = length(planeDir);

         float fade = 1.0;

         if (planeLen > 1e-5) {
           planeDir /= planeLen;

           vec3 awayDir = (uFlip > 0.5) ? -planeDir : planeDir;

           float d = max(0.0, dot(vWorldPos - uTarget, awayDir));
           float t = clamp(
             (d - uFadeStart) / max(1e-5, (uFadeEnd - uFadeStart)),
             0.0,
             1.0
           );

           t = smooth01(t);
           t = pow(t, uFadePower);
           fade = 1.0 - t;
         }`,
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        /getShadowMask\(\)/g,
        "mix(1.0, getShadowMask(), fade)",
      );

      shaderRef.current = shader;
    };

    mat.needsUpdate = true;
  }, [targetVec, fadeStart, fadeEnd, fadePower, flipFadeDirection]);

  /* ------------------------------------------------------------------ */
  /*  Update uniforms every frame (safe + deterministic)                */
  /* ------------------------------------------------------------------ */

  useFrame(() => {
    const light = lightRef?.current;
    const shader = shaderRef.current;

    if (!light || !shader) return;

    const dir = new THREE.Vector3()
      .copy(targetVec)
      .sub(light.position)
      .normalize();

    shader.uniforms.uLightDir.value.copy(dir);
    shader.uniforms.uTarget.value.copy(targetVec);
    shader.uniforms.uFadeStart.value = fadeStart;
    shader.uniforms.uFadeEnd.value = fadeEnd;
    shader.uniforms.uFadePower.value = fadePower;
    shader.uniforms.uFlip.value = flipFadeDirection ? 1.0 : 0.0;
  });

  /* ------------------------------------------------------------------ */

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, y, 0]}
      receiveShadow
      castShadow={false}
      renderOrder={-1}
    >
      <planeGeometry args={[size, size]} />
      <shadowMaterial
        ref={matRef}
        transparent
        opacity={opacity}
        polygonOffset
        polygonOffsetFactor={-1}
        polygonOffsetUnits={-1}
      />
    </mesh>
  );
}

export function RendererSetup({
  renderEnabled,
  shadowType,
  enableHelperLayerPicking = true,
  isNight,
}) {
  const { gl, camera, raycaster, invalidate } = useThree();

  useEffect(() => {
    // Match your previous onCreated setup
    gl.outputColorSpace = THREE.SRGBColorSpace;
    gl.toneMapping = THREE.NoToneMapping;
    // gl.toneMappingExposure = isNight ? 0.6 : 1.6;

    gl.shadowMap.enabled = !!renderEnabled;
    gl.shadowMap.type = shadowType;

    // Layers setup (same as before)
    camera.layers.enable(0);
    camera.layers.disable(1);

    if (enableHelperLayerPicking) {
      raycaster.layers.enable(1);
    }

    invalidate?.();
  }, [
    gl,
    camera,
    raycaster,
    invalidate,
    renderEnabled,
    shadowType,
    enableHelperLayerPicking,
  ]);

  return null;
}

/* =============================================================================
   RenderStack: the one component your dev file uses
============================================================================= */
function isPresetMeduimOrLower(effectivePresetId) {
  return effectivePresetId === "medium" || effectivePresetId === "low";
}

export function RenderStack({
  SceneEnvPath,

  // Refs from outer file
  controlsRef,
  dirLightRef,

  // Quality state from outer file
  presetId,
  setPresetId,
  forcedPresetId,
  setForcedPresetId,

  // Master render switch
  renderEnabled,
  setRenderEnabled,

  // Background controls (same as your current defaults)
  backgroundMode = "blur",
  backgroundBlur = 0.8,
  domeAnglesRef,
  hdriOffsetRef,
  isNight = false,
}) {
  const { gl } = useThree();
  // const [isNight, setIsNight] = useState(false);
  const effectivePresetId = forcedPresetId || presetId;
  const preset = QUALITY_PRESETS[effectivePresetId];
  const shadowKey = `${effectivePresetId}-${preset.shadows.mapSize}`;

  // Keep renderer shadow settings in sync (onCreated runs only once)
  useEffect(() => {
    if (!gl) return;
    gl.shadowMap.enabled = !!renderEnabled;
    gl.shadowMap.type = preset.shadows.type;
    gl.toneMappingExposure = isNight
      ? isPresetMeduimOrLower(effectivePresetId)
        ? 0.35
        : 0.7
      : 1.0;
  }, [
    gl,
    renderEnabled,
    preset.shadows.type,
    isNight,
    forcedPresetId,
    presetId,
    effectivePresetId,
  ]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable)
        return;

      // 5 = toggle high performance
      // if (e.key === "5") {
      //   e.preventDefault();
      //   e.stopPropagation();
      //   setRenderEnabled((v) => !v);
      //   return;
      // }

      if (e.key === "1") setForcedPresetId("low");
      if (e.key === "2") setForcedPresetId("medium");
      if (e.key === "3") setForcedPresetId("high");
      if (e.key === "4") setForcedPresetId("ultra");

      if (e.key === "0") setForcedPresetId(null);

      if (e.key === "[") {
        setForcedPresetId((cur) => {
          const base = cur || effectivePresetId;
          return nextLowerPresetId(base);
        });
      }
      if (e.key === "]") {
        setForcedPresetId((cur) => {
          const base = cur || effectivePresetId;
          return nextHigherPresetId(base);
        });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [effectivePresetId, setForcedPresetId, setRenderEnabled]);

  return (
    <>
      <RendererSetup
        renderEnabled={renderEnabled}
        shadowType={preset.shadows.type}
        isNight={isNight}
      />
      {!forcedPresetId && (
        <AdaptiveQualityController
          presetId={presetId}
          setPresetId={setPresetId}
          config={ADAPTIVE_QUALITY_CONFIG}
        />
      )}

      <PerfHud
        presetId={effectivePresetId}
        forcedPresetId={forcedPresetId}
        renderEnabled={renderEnabled}
        enabled
      />

      {!renderEnabled && <FallbackIBL intensity={1.2} isNight={isNight} />}

      {renderEnabled && (
        <>
          {preset.softShadows.enabled && (
            <SoftShadows
              focus={preset.softShadows.focus}
              samples={preset.softShadows.samples}
              size={preset.softShadows.size}
            />
          )}
          {isNight && <ambientLight intensity={0.15} color="#6580f7" />}

          {/* <LightDomeOrbit
            key={shadowKey}
            lightRefExternal={dirLightRef}
            controlsRef={controlsRef}
            anglesRef={domeAnglesRef}
            hdriOffsetRef={hdriOffsetRef}
            target={[0, 0, 0]}
            lightRadius={180}
            helperRadius={70}
            intensity={2}
            shadowProps={{
              "shadow-mapSize-width": preset.shadows.mapSize,
              "shadow-mapSize-height": preset.shadows.mapSize,
              "shadow-camera-near": 1,
              "shadow-camera-far": 300,
              "shadow-camera-left": -60,
              "shadow-camera-right": 60,
              "shadow-camera-top": 60,
              "shadow-camera-bottom": -60,
              "shadow-bias": -0.005,
              "shadow-normalBias": 0.001,
            }}
          /> */}

          <CameraAttachedLight
            lightRefExternal={dirLightRef}
            isNight={isNight}
            effectivePresetId={effectivePresetId}
          />

          <EnvWithLightProbe
            SceneEnvPath={SceneEnvPath}
            background={true}
            probeIntensity={
              isNight
                ? isPresetMeduimOrLower(effectivePresetId)
                  ? 0.2
                  : 0.9
                : 1.5
            }
            isNight={isNight}
            effectivePresetId={effectivePresetId}
            domeAnglesRef={domeAnglesRef}
            hdriOffsetRef={hdriOffsetRef}
            backgroundMode={backgroundMode}
            backgroundBlur={backgroundBlur}
          />

          <ShadowCatcherConeFadePlane
            lightRef={dirLightRef}
            target={[0, 0, 0]}
            size={3000}
            y={-25.5}
            opacity={
              isNight
                ? isPresetMeduimOrLower(effectivePresetId)
                  ? 0.25
                  : 0.2
                : 0.15
            }
            fadeStart={20}
            fadeEnd={60}
            fadePower={1.0}
            debug={true}
          />
        </>
      )}

      <AutoDOF
        key={`${effectivePresetId}-${isNight}`}
        effectivePresetId={effectivePresetId}
        isNight={isNight}
        preset={preset}
        enabled={
          renderEnabled &&
          (effectivePresetId === "high" || effectivePresetId === "ultra")
        }
      />
    </>
  );
}
